/**
 * Decision-evidence capture util — collects a snapshot of every widget instance
 * visible on the dashboard at submit time, along with their current output.
 *
 * The backend's `_fire_reasoning_capture` hook (services/reasoning_capture.py)
 * reads three keys out of TaskProgress.data after each progress write:
 *
 *   - `visibleWidgetOutputs`  — list of {widgetInstanceId, widgetType,
 *                                        dashboardDataKey, output}
 *   - `decisionWidgetId`      — the widget that captured the final decision
 *                               (Conviction / Scorecard / TradingScore if present)
 *   - `decisionValue`         — that widget's output as the structured decision
 *
 * It hashes + truncates the output server-side, persists a `decision_evidence`
 * row, and strips these three keys from the reasoning rollup so they don't
 * pollute the aggregated content (see _aggregate_assignment_data).
 *
 * Frontend responsibility: build the payload and merge it into the data
 * argument of updateStepProgress / completeStep. That's it — no hashing,
 * no truncation, no API call here.
 */
import type {
  DashboardWidgetInstance,
  StepWidgetConfigs,
  WidgetTemplate,
} from '../../../types';

/** Decision-class widget types — output is the structured decision. */
const DECISION_WIDGET_TYPES = new Set(['conviction', 'scorecard', 'trading_score']);

export interface VisibleWidgetSnapshot {
  widgetInstanceId: string;
  widgetType: string;
  dashboardDataKey: string | null;
  output: unknown;
}

export interface LineageCarriers {
  visibleWidgetOutputs: VisibleWidgetSnapshot[];
  decisionWidgetId?: string;
  decisionValue?: unknown;
}

/**
 * Resolve a widget instance's dataKey using the same precedence as the
 * dashboard renderer: step-level widgetConfig override > dashboard-level
 * configOverrides.dataKey > the instance id itself as a fallback.
 */
function resolveDataKey(
  widget: DashboardWidgetInstance,
  stepWidgetConfigs?: StepWidgetConfigs,
): string {
  const stepCfg = stepWidgetConfigs?.widgets?.[widget.id];
  const stepDk = (stepCfg as { dataKey?: string } | undefined)?.dataKey;
  if (stepDk) return stepDk;
  const dashDk = (widget.configOverrides as Record<string, unknown> | undefined)?.dataKey;
  if (typeof dashDk === 'string' && dashDk) return dashDk;
  return widget.id;
}

/**
 * Build the lineage carriers for one save / complete call.
 *
 * Pure: never mutates inputs, returns a fresh object. Returns
 * { visibleWidgetOutputs: [] } if there's nothing meaningful to snapshot.
 */
export function captureVisibleOutputs(
  dashboardData: Record<string, unknown>,
  dashboardWidgets: DashboardWidgetInstance[],
  widgetTemplates: Map<string, WidgetTemplate>,
  stepWidgetConfigs?: StepWidgetConfigs,
): LineageCarriers {
  const visibleWidgetOutputs: VisibleWidgetSnapshot[] = [];
  let decisionWidgetId: string | undefined;
  let decisionValue: unknown;

  for (const widget of dashboardWidgets) {
    const tpl = widgetTemplates.get(widget.templateId);
    if (!tpl) continue; // template hasn't loaded — skip rather than guess
    const dataKey = resolveDataKey(widget, stepWidgetConfigs);
    const output = dashboardData[dataKey];
    // Skip widgets with no output yet — they weren't meaningfully "visible
    // and engaged" in the analyst's evidence chain.
    if (output === undefined || output === null) continue;

    visibleWidgetOutputs.push({
      widgetInstanceId: widget.id,
      widgetType: tpl.type,
      dashboardDataKey: dataKey,
      output,
    });

    // First Decision-class widget with output wins. Conviction is the
    // strongest decision signal; if multiple are present they're recorded
    // in visibleWidgetOutputs and the analyst can disambiguate downstream.
    if (decisionWidgetId === undefined && DECISION_WIDGET_TYPES.has(tpl.type)) {
      decisionWidgetId = tpl.type;
      decisionValue = output;
    }
  }

  return { visibleWidgetOutputs, decisionWidgetId, decisionValue };
}

/**
 * Merge lineage carriers into a dashboardData payload, ready for
 * updateStepProgress / completeStep. The carriers occupy three reserved
 * keys; the backend's _aggregate_assignment_data explicitly skips them
 * when building the reasoning rollup, so they never pollute the canonical
 * field set.
 */
export function withLineageCarriers(
  dashboardData: Record<string, unknown>,
  carriers: LineageCarriers,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {
    ...dashboardData,
    visibleWidgetOutputs: carriers.visibleWidgetOutputs,
  };
  if (carriers.decisionWidgetId !== undefined) {
    merged.decisionWidgetId = carriers.decisionWidgetId;
  }
  if (carriers.decisionValue !== undefined) {
    merged.decisionValue = carriers.decisionValue;
  }
  return merged;
}
